// Proportional unit conversion for a line-item Rate.
//
// A line's Rate is "price per one <unit>". Switching Unit must not touch the
// line's actual value — 500 ML at ₹200/LTR is worth the same ₹100 whether the
// row currently reads "1 LTR → ₹200" or "500 ML → ₹0.2" — so this converts
// the RATE by the same factor that separates the two units, leaving every
// totals formula in the app (qty × unitPrice, unchanged everywhere) to land
// on the correct total automatically. No line schema anywhere needs a new
// field for this: the existing quantity/unit/unitPrice triple is enough.
//
// Only units within the same physical quantity (volume, or weight) convert
// against each other. A container unit (Drum, Bucket, Can, Box …) has no
// universal fill volume — "1 Bucket" could be 20 L or 5 L depending on the
// product — so switching to/from one of those leaves Rate untouched, exactly
// like today's free-text Unit field always did.

export interface UnitDef {
  value: string;
  label: string;
  /** How many of the family's smallest unit equal one of this unit. */
  factor: number;
}

export const VOLUME_UNITS: UnitDef[] = [
  { value: 'ML', label: 'ML (Millilitre)', factor: 1 },
  { value: 'LTR', label: 'LTR (Litre)', factor: 1000 },
  { value: 'KL', label: 'KL (Kilolitre)', factor: 1_000_000 },
];

export const WEIGHT_UNITS: UnitDef[] = [
  { value: 'G', label: 'G (Gram)', factor: 1 },
  { value: 'KG', label: 'KG (Kilogram)', factor: 1000 },
  { value: 'QUINTAL', label: 'Quintal', factor: 100_000 },
  { value: 'TON', label: 'Ton', factor: 1_000_000 },
];

/**
 * Count unit a rep can pick by hand (client, 2026-10-03): NOS — so many
 * numbers / pieces. No conversion factor (switching to or from it leaves the
 * Rate as typed), and unlike the packaging containers below it is never
 * auto-replaced from the pack size — see UnitPackCell.
 */
export const COUNT_UNITS: UnitDef[] = [
  { value: 'NOS', label: 'NOS (Numbers)', factor: 0 },
];

/**
 * Container/count units — no fixed fill volume, so never convertible.
 *
 * Values match the GST UQC codes abbreviatePackaging() (packagingAbbreviation.ts)
 * now writes onto a line when a catalogue product is picked (Box -> BOX,
 * Bucket -> BKT, Barrel -> BRL) — same vocabulary in both places, so a line
 * picked via the Product Selector and then reopened here shows its real unit
 * selected, instead of two differently-spelled entries for the same pack
 * type. The legacy BX/BKT/DRUM/BBL entries are kept alongside the UQC ones
 * (not replaced) purely so findUnit() still recognises a value an
 * already-stored line wrote before this normalisation — never offered as a
 * new choice anywhere (PACK_UNITS isn't in UNIT_GROUPS at all, see below).
 */
export const PACK_UNITS: UnitDef[] = [
  { value: 'PCS', label: 'PCS (Piece)', factor: 0 },
  { value: 'NOS', label: 'NOS', factor: 0 },
  { value: 'BOX', label: 'Box', factor: 0 },
  { value: 'BKT', label: 'Bucket', factor: 0 },
  { value: 'CAN', label: 'Can', factor: 0 },
  { value: 'PAC', label: 'Pack (Bucket/Pouch)', factor: 0 },
  { value: 'DRM', label: 'Drum (Barrel)', factor: 0 },
  { value: 'BRL', label: 'Barrel', factor: 0 },
  { value: 'BTL', label: 'Bottle', factor: 0 },
  { value: 'OTH', label: 'Other', factor: 0 },
  // Legacy spellings from before the GST UQC normalisation.
  { value: 'BX', label: 'Box (legacy)', factor: 0 },
  { value: 'BKT', label: 'Bucket (legacy)', factor: 0 },
  { value: 'DRUM', label: 'Drum (legacy)', factor: 0 },
  { value: 'BBL', label: 'Barrel (legacy)', factor: 0 },
];

// PACK_UNITS is deliberately NOT offered here. The packaging container
// (Box/Bucket/Can/Barrel) is derived from the pack's own size — RBM's fixed
// bands, see packagingAbbreviation.ts's containerPackagingType() — never a
// free pick, so a rep can no longer tag a 210 LTR barrel as "Box" by
// mistake. UnitPackCell auto-corrects it from packSize instead of listing
// it here. PACK_UNITS stays defined below only so ALL_UNITS/findUnit still
// recognise a value an older line already carries (e.g. "BX", "DRUM").
export const UNIT_GROUPS: { label: string; units: UnitDef[] }[] = [
  { label: 'Volume', units: VOLUME_UNITS },
  { label: 'Weight', units: WEIGHT_UNITS },
  { label: 'Count', units: COUNT_UNITS },
];

const ALL_UNITS = [...VOLUME_UNITS, ...WEIGHT_UNITS, ...COUNT_UNITS, ...PACK_UNITS];

function findUnit(unit: string): UnitDef | undefined {
  const key = String(unit || '').trim().toUpperCase();
  return ALL_UNITS.find((u) => u.value === key);
}

/**
 * Rate for `newUnit` that represents the same per-piece value as `rate` does
 * for `oldUnit`. Returns `rate` unchanged whenever the two units aren't in
 * the same convertible family (a container unit either side, an unrecognised
 * unit, or no real change) — there's nothing to convert, so the existing
 * value is left exactly as the rep set it.
 */
export function convertRateForUnit(oldUnit: string, newUnit: string, rate: number): number {
  const from = findUnit(oldUnit);
  const to = findUnit(newUnit);
  if (!from || !to || !from.factor || !to.factor) return rate;
  const isSameFamily = VOLUME_UNITS.includes(from) === VOLUME_UNITS.includes(to)
    && WEIGHT_UNITS.includes(from) === WEIGHT_UNITS.includes(to);
  if (!isSameFamily) return rate;
  const pricePerSmallestUnit = (Number(rate) || 0) / from.factor;
  const converted = pricePerSmallestUnit * to.factor;
  // Round to a sensible precision — 6 places covers a ₹200/LTR product priced
  // down to the ML (₹0.2) without the display filling up with float noise.
  return Math.round(converted * 1_000_000) / 1_000_000;
}
